/**
 * Partial Report Manager
 * 
 * Handles the UI modifications for partial reports, including:
 * - Diagram masking with semi-transparent overlay
 * - Adding CTA buttons to schedule a meeting
 * - Hiding specific parts of diagram
 */

interface AccessMetadata {
  isFullAccess: boolean;
  hasAccessKey: boolean;
}

interface ReportMetadata {
  access?: AccessMetadata;
  [key: string]: any;
}

interface TabChangedEvent extends CustomEvent {
  detail: {
    tabId: string;
    [key: string]: any;
  };
}

// Initialize the partial report manager
class PartialReportManager {
  isFullAccess: boolean = true; // Default to full access until we know otherwise
  hasAccessKey: boolean = false; // Whether an access key was provided
  initialized: boolean = false;
  overlayAdded: boolean = false;
  ctaButtonsAdded: boolean = false;
  
  /**
   * Initialize with metadata from server response
   * @param metadata Contains access level information
   */
  initialize(metadata: ReportMetadata | null | undefined): void {
    if (!metadata || !metadata.access) {
      this.isFullAccess = true; // Default to full access if no metadata
      return;
    }
    
    this.isFullAccess = metadata.access.isFullAccess;
    this.hasAccessKey = metadata.access.hasAccessKey;
    this.initialized = true;
    
    // Apply partial report UI if needed
    if (!this.isFullAccess) {
      this.applyPartialReportUI();
      
      // Initialize the more content indicator with new silhouettes
      if (typeof (window as any).moreContentIndicator?.initialize === 'function') {
        (window as any).moreContentIndicator.initialize(metadata);
      }
      
      // Extend the tips formatter to handle silhouettes
      this.extendTipsFormatter();
    }
  }
  
  /**
   * Generate category-specific silhouette tips for partial reports
   * @returns Record of category names to arrays of silhouette tip texts
   */
  generateSilhouetteTips(): Record<string, string[]> {
    return {
      'startup': [
        'Optimal product onboarding strategy'
      ],
      'cross_sell': [
        'Revenue expansion opportunity'
      ],
      'common_problems': [
        'Critical issue prevention strategy'
      ],
      'usage_maintenance': [
        'Long-term engagement approach'
      ]
    };
  }
  
  /**
   * Extend the tips formatter to handle silhouettes
   */
  private extendTipsFormatter(): void {
    if (!(window as any).tipsFormatter) {
      console.warn('Tips formatter not available, silhouettes will not be added');
      return;
    }
    
    // Store the original formatContent function
    const originalFormatContent = (window as any).tipsFormatter.formatContent;
    
    // Replace with our enhanced version
    (window as any).tipsFormatter.formatContent = (content: any) => {
      // Only add silhouettes if we're in partial mode and have structured data
      if (!this.isFullAccess && typeof content === 'object' && content !== null) {
        
        // Generate silhouette tips
        const silhouettes = this.generateSilhouetteTips();
        
        // Add a silhouettes property to the content
        const contentWithSilhouettes = { ...content, _silhouettes: silhouettes };
        
        // Return formatted content with silhouettes
        return originalFormatContent.call((window as any).tipsFormatter, contentWithSilhouettes);
      }
      
      // Otherwise, use the original formatter
      return originalFormatContent.call((window as any).tipsFormatter, content);
    };
  }
  
  /**
   * Apply all UI modifications for partial reports
   */
  applyPartialReportUI(): void {
    // Apply modifications when the DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.addDiagramMask();
        this.addCtaButtons();
      });
    } else {
      this.addDiagramMask();
      this.addCtaButtons();
    }
    
    // Also listen for tab changes to ensure masking is applied
    document.addEventListener('tabChanged', ((event: TabChangedEvent) => {
      if (event.detail.tabId === 'journey') {
        // Wait for the diagram to render
        setTimeout(() => this.addDiagramMask(), 300);
      }
      
      if (event.detail.tabId === 'tips') {
        // Ensure CTA button is visible on tips tab
        this.addCtaButtons();
        
        // Refresh the content indicators and silhouettes
        if (typeof (window as any).moreContentIndicator?.addMoreContentIndicators === 'function') {
          setTimeout(() => (window as any).moreContentIndicator.addMoreContentIndicators(), 200);
        }
      }
    }) as EventListener);
  }
  
  /**
   * Add semi-transparent mask to the bottom half of the diagram
   */
  addDiagramMask(): void {
    if (this.isFullAccess || this.overlayAdded) return;
    
    const diagramContainer = document.getElementById('diagramContainer');
    if (!diagramContainer) return;
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'partialReportOverlay';
    overlay.className = 'partial-report-overlay';
    
    // Create inner container with solid background
    const innerContainer = document.createElement('div');
    innerContainer.className = 'overlay-inner-container';
    
    // Add text
    const textElement = document.createElement('div');
    textElement.className = 'overlay-message';
    textElement.textContent = 'View full journey by scheduling a meeting with our product experts.';
    
    // Add CTA button
    const button = document.createElement('button');
    button.className = 'brand-button-compact';
    button.textContent = 'Schedule a Meeting';
    button.onclick = () => this.openScheduleForm();
    
    innerContainer.appendChild(textElement);
    innerContainer.appendChild(button);
    overlay.appendChild(innerContainer);
    
    // Add overlay to container
    diagramContainer.style.position = 'relative';
    diagramContainer.appendChild(overlay);
    
    this.overlayAdded = true;
  }
  
  /**
   * Show CTA area for locked engagement tips
   */
  addCtaButtons(): void {
    if (this.isFullAccess || this.ctaButtonsAdded) return;
    
    // Show the CTA area that's already in the HTML
    const ctaArea = document.querySelector('.tips-cta-area') as HTMLElement;
    if (ctaArea) {
      ctaArea.style.display = 'block';
      
      // Set up the click handler for the existing button
      const ctaButton = document.getElementById('tipsCtaButton');
      if (ctaButton) {
        ctaButton.onclick = () => this.openScheduleForm();
      }
    }
    
    this.ctaButtonsAdded = true;
  }
  
  /**
   * Open the schedule form
   */
  openScheduleForm(): void {
    // Always open the URL directly instead of using the lead form modal
    window.open("https://blustream.io/contact-sales?utm_source=pxai_journey&utm_medium=internal&utm_campaign=13746024-PXAI%20Journey%20Builder", '_blank');
  }
}

// Create global instance
(window as any).partialReportManager = new PartialReportManager();